'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Users,
  FileText,
  Tag,
  Globe,
  Lock,
  Plus,
  X,
  GripVertical,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import { useCommunities } from '../../hooks/use-communities';
import { generateSlug } from '../../utils/slug';
import { setSelectedCommunity } from '../../utils/community-cookie';
import { sanitizeInput } from '../../utils/sanitize-html';
import { toast } from 'sonner';

const COMMUNITY_CATEGORIES = [
  'Tech',
  'Dev Tools',
  'Gaming',
  'AI',
  'Support',
  'Media',
  'Open Source',
  'Hardware',
  'Finance',
  'Education',
  'Lifestyle',
  'Other',
];

export default function CreateCommunityPage() {
  const { walletAddress, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { useCreateCommunity } = useCommunities();
  const createCommunityMutation = useCreateCommunity();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    rules: '',
    website: '',
    isPrivate: false,
  });

  const [discussionCategories, setDiscussionCategories] = useState([
    {
      name: 'General Discussion',
      description: 'General discussions and topics',
      color: '#8B5CF6',
    },
    {
      name: 'Getting Started',
      description: 'Help for new members',
      color: '#10B981',
    },
  ]);

  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#8B5CF6',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCategoryChange = (value) => {
    setFormData((prev) => ({ ...prev, category: value }));
  };

  const addDiscussionCategory = () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    if (discussionCategories.length >= 20) {
      toast.error('Maximum 20 categories allowed');
      return;
    }

    setDiscussionCategories((prev) => [
      ...prev,
      {
        name: newCategory.name.trim(),
        description: newCategory.description.trim(),
        color: newCategory.color || '#8B5CF6',
      },
    ]);
    setNewCategory({ name: '', description: '', color: '#8B5CF6' });
  };

  const removeDiscussionCategory = (index) => {
    setDiscussionCategories((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // Clear previous errors

    // Check if wallet is connected
    if (!walletAddress) {
      toast.error('Please connect your wallet first');
      return;
    }

    // Sanitize all text inputs
    const sanitizedName = sanitizeInput(formData.name.trim());
    const sanitizedDescription = sanitizeInput(formData.description.trim());
    const sanitizedRules = formData.rules.trim()
      ? sanitizeInput(formData.rules.trim())
      : null;
    const sanitizedWebsite = formData.website.trim()
      ? sanitizeInput(formData.website.trim())
      : null;

    // Log sanitization results if data was modified
    if (formData.name.trim() !== sanitizedName) {
      console.warn('⚠️ Name was sanitized:', {
        original: formData.name.trim(),
        sanitized: sanitizedName,
      });
    }
    if (formData.description.trim() !== sanitizedDescription) {
      console.warn('⚠️ Description was sanitized:', {
        original: formData.description.trim().substring(0, 50) + '...',
        sanitized: sanitizedDescription.substring(0, 50) + '...',
      });
    }

    // Validation with detailed error messages
    const newErrors = {};

    if (!sanitizedName) {
      newErrors.name = 'Community name is required';
    } else if (sanitizedName.length < 3) {
      newErrors.name = 'Community name must be at least 3 characters';
    } else if (sanitizedName.length > 100) {
      newErrors.name = 'Community name must not exceed 100 characters';
    }

    if (!sanitizedDescription) {
      newErrors.description = 'Description is required';
    } else if (sanitizedDescription.length < 10) {
      newErrors.description = `Description must be at least 10 characters (currently ${sanitizedDescription.length})`;
    } else if (sanitizedDescription.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }

    if (discussionCategories.length === 0) {
      newErrors.categories = 'Add at least one discussion category';
    }

    // Validate website URL if provided
    if (sanitizedWebsite) {
      try {
        const url = new URL(sanitizedWebsite);
        if (!['http:', 'https:'].includes(url.protocol)) {
          newErrors.website = 'Website must be a valid HTTP or HTTPS URL';
        }
      } catch {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    // If there are validation errors, show them and stop
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Generate slug from sanitized name
      const slug = generateSlug(sanitizedName);

      // Sanitize discussion categories
      const sanitizedCategories = discussionCategories.map((cat, index) => ({
        name: sanitizeInput(cat.name.trim()),
        description: sanitizeInput(cat.description?.trim() || ''),
        color:
          cat.color && /^#[0-9A-F]{6}$/i.test(cat.color)
            ? cat.color
            : '#8B5CF6',
        order: index,
      }));

      const payload = {
        name: sanitizedName,
        slug,
        description: sanitizedDescription,
        category: formData.category,
        rules: sanitizedRules,
        website: sanitizedWebsite,
        isPrivate: formData.isPrivate,
        discussionCategories: sanitizedCategories,
        creator: walletAddress,
      };

      // Log the payload for debugging
      console.log('📤 Creating community with data:', {
        ...payload,
        creator: `${walletAddress.slice(0, 6)}...${walletAddress.slice(
          -4
        )}`,
      });

      const result = await createCommunityMutation.mutateAsync(payload);

      toast.success('Community created successfully!');

      const newCommunity = result?.community || result;
      if (newCommunity) {
        // Set cookie and navigate to the new community
        setSelectedCommunity(
          newCommunity.slug || newCommunity.oid,
          newCommunity.oid
        );
        navigate(`/forum/${newCommunity.slug || newCommunity.oid}`);
      }
    } catch (error) {
      console.error('Error creating community:', error);
      toast.error(
        error.response?.data?.message || 'Failed to create community'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10 container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            variant="ghost"
            onClick={() => navigate('/forum')}
            className="mb-4 text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Communities
          </Button>
          <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
            Create Your Community
          </h1>
          <p className="text-zinc-400 text-lg">
            Build your own space on Talktrail and bring people together
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          {/* Basic Information Section */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 backdrop-blur-xl p-6 space-y-6">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-400" />
              Basic Information
            </h2>

            {/* Community Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                Community Name *
              </label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Web3 Developers"
                className={`bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 ${
                  errors.name ? 'border-red-500 focus:border-red-500' : ''
                }`}
                maxLength={100}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-500">
                  {formData.name.length}/100 characters
                </p>
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300">
                Description *
              </label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="What is your community about? (Minimum 10 characters)"
                className={`bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 min-h-[100px] ${
                  errors.description
                    ? 'border-red-500 focus:border-red-500'
                    : ''
                }`}
                maxLength={500}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-500">
                  {formData.description.length}/500 characters (min 10)
                </p>
                {errors.description && (
                  <p className="text-xs text-red-400">{errors.description}</p>
                )}
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Tag className="w-4 h-4 text-violet-400" />
                Main Category *
              </label>
              {errors.category && (
                <p className="text-xs text-red-400">{errors.category}</p>
              )}
              <Select
                value={formData.category}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {COMMUNITY_CATEGORIES.map((cat) => (
                    <SelectItem
                      key={cat}
                      value={cat}
                      className="text-white hover:bg-white/10"
                    >
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Website (Optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                <Globe className="w-4 h-4 text-violet-400" />
                Website (optional)
              </label>
              <Input
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://your-community.com"
                className={`bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 ${
                  errors.website ? 'border-red-500 focus:border-red-500' : ''
                }`}
              />
              {errors.website && (
                <p className="text-xs text-red-400">{errors.website}</p>
              )}
            </div>
          </div>

          {/* Discussion Categories Section */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 backdrop-blur-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-400" />
                  Discussion Categories
                </h2>
                {errors.categories && (
                  <p className="text-xs text-red-400 mt-1">
                    {errors.categories}
                  </p>
                )}
              </div>
              <span className="text-sm text-zinc-500">
                {discussionCategories.length} categories
              </span>
            </div>
            <p className="text-sm text-zinc-400">
              Add categories to organize discussions in your community (e.g.,
              General Discussion, Getting Started, Help & Support)
            </p>

            {/* Existing Categories */}
            <div className="space-y-2">
              {discussionCategories.map((cat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start gap-3 p-4 rounded-lg bg-white/5 border border-white/10 group"
                >
                  <GripVertical className="w-5 h-5 text-zinc-500 mt-1 flex-shrink-0" />
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0 mt-1.5 ring-2 ring-white/20"
                    style={{ backgroundColor: cat.color || '#8B5CF6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white">{cat.name}</p>
                    {cat.description && (
                      <p className="text-sm text-zinc-400 mt-1">
                        {cat.description}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDiscussionCategory(index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Add New Category */}
            <div className="space-y-3 p-4 rounded-lg bg-violet-500/5 border border-violet-500/20">
              <div className="space-y-2">
                <Input
                  value={newCategory.name}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Category name (e.g., General Discussion)"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500"
                  maxLength={50}
                />
                <Input
                  value={newCategory.description}
                  onChange={(e) =>
                    setNewCategory((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Description (optional)"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500"
                  maxLength={200}
                />
                <div className="flex items-center gap-2">
                  <label className="text-sm text-zinc-400">Color:</label>
                  <input
                    type="color"
                    value={newCategory.color}
                    onChange={(e) =>
                      setNewCategory((prev) => ({
                        ...prev,
                        color: e.target.value,
                      }))
                    }
                    className="w-12 h-10 rounded cursor-pointer bg-white/5 border border-white/10"
                  />
                  <div
                    className="w-4 h-4 rounded-full ring-2 ring-white/20"
                    style={{ backgroundColor: newCategory.color }}
                  />
                  <span className="text-xs text-zinc-500 font-mono">
                    {newCategory.color}
                  </span>
                </div>
              </div>
              <Button
                type="button"
                onClick={addDiscussionCategory}
                variant="outline"
                className="w-full border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </div>
          </div>

          {/* Community Rules Section */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 backdrop-blur-xl p-6 space-y-6">
            <h2 className="text-xl font-semibold text-white">
              Community Rules (optional)
            </h2>
            <Textarea
              name="rules"
              value={formData.rules}
              onChange={handleInputChange}
              placeholder="Set guidelines for your community members..."
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 min-h-[120px]"
              maxLength={1000}
            />
            <p className="text-xs text-zinc-500">
              {formData.rules.length}/1000 characters
            </p>
          </div>

          {/* Privacy Settings Section */}
          <div className="rounded-2xl border border-white/10 bg-zinc-900/50 backdrop-blur-xl p-6 space-y-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-violet-400" />
              Privacy Settings
            </h2>
            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
              <div>
                <p className="text-sm font-medium text-white">
                  Private Community
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  Only approved members can see and post
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="isPrivate"
                  checked={formData.isPrivate}
                  onChange={handleInputChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/forum')}
              className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Community...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Community
                </>
              )}
            </Button>
          </div>
        </motion.form>
      </main>

      <Footer />
    </div>
  );
}
